// web-vite/src/components/Sidebar.tsx
//
// Mirrors src/navigation/Sidebar.tsx exactly:
//   - 80px wide, dark sidebarBg, right border
//   - "R" logo at top (40x40 red square)
//   - Menu items with custom PNG icons (28x28), 2-line label, ~12px text
//   - Active: left 3px primary border + primaryBg + gold text
//   - Bottom logo (44x44 circle)
//
// Mobile uses Platform.OS check to swap @1x/@2x/@3x PNGs; on web we use the
// _hd variants that were copied into /public/icons during P3.1 setup.

import React from 'react'
import {NavLink} from 'react-router-dom'
import clsx from 'clsx'
import {Activity, MapPin, PlugZap, Wifi, type LucideProps} from 'lucide-react'

interface MenuItem {
  /** Route path used by NavLink. */
  to: string
  /** Display label — `\n` becomes two lines, matching mobile design. */
  label: string
  /** PNG asset path in /public/icons (preferred — exact mobile parity). */
  iconSrc?: string
  /** Lucide fallback when no PNG exists (e.g. Locations). */
  IconComponent?: React.ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
  >
}

// Primary nav items. Activity Feed sits just above Dev API as a utility
// view alongside the supporting tools, leaving the domain screens (Tag
// Board → Personnel → Tag Assets, etc.) at the top of the rail.
const MENU: MenuItem[] = [
  {to: '/',           label: 'Tag Board',         iconSrc: '/icons/tagBoard.png'},
  {to: '/chambers',   label: 'Safety\nChamber',   iconSrc: '/icons/safetyChamber.png'},
  {to: '/locations',  label: 'Locations',         IconComponent: MapPin},
  {to: '/personnel',  label: 'Personnel',         iconSrc: '/icons/personnel.png'},
  // Tag Assets hidden from the sidebar per product direction. The
  // /equipment route is still mounted in App.tsx so direct URL
  // navigation, deep links from other screens, and CRUD pages still
  // work; only the primary nav entry is suppressed.
  // {to: '/equipment',  label: 'Tag Assets',     iconSrc: '/icons/tagAssets.png'},
  {to: '/access-points', label: 'Access\nPoints', IconComponent: Wifi},
  {to: '/activity',      label: 'Activity\nFeed', IconComponent: Activity},
  {to: '/dev-api',       label: 'Dev\nAPI',       IconComponent: PlugZap},
]

export const Sidebar: React.FC = () => {
  return (
    <aside className="flex h-full w-sidebar shrink-0 flex-col items-center border-r border-surfaceBorder bg-sidebarBg py-lg">
      {/* "R" logo — brand-red square with white "R" */}
      <div className="mb-xxl flex h-10 w-10 items-center justify-center rounded-md bg-brandRed">
        <span className="text-xl font-bold text-white">R</span>
      </div>

      {/* Menu items */}
      <nav className="flex w-full flex-1 flex-col items-center">
        {MENU.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({isActive}) =>
              clsx(
                'mb-sm flex w-[68px] flex-col items-center justify-center rounded-md py-md',
                'border-l-[3px] border-l-transparent',
                isActive && 'border-l-sidebarActiveBorder bg-sidebarActive',
              )
            }
          >
            {({isActive}) => (
              <SidebarItemContent item={item} isActive={isActive} />
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom logo */}
      <div className="flex items-center justify-center pb-md">
        <img
          src="/icons/logo.png"
          alt="Logo"
          className="h-11 w-11 rounded-full object-contain"
        />
      </div>
    </aside>
  )
}

const SidebarItemContent: React.FC<{item: MenuItem; isActive: boolean}> = ({
  item,
  isActive,
}) => {
  // Match mobile tintColor behavior: active = gold, inactive = sidebarIcon gray.
  const tint = isActive ? 'var(--color-sidebarIconActive)' : 'var(--color-sidebarIcon)'

  return (
    <>
      <div className="flex h-7 w-7 items-center justify-center">
        {item.iconSrc ? (
          // CSS mask gives us tintColor parity without per-state PNGs.
          <span
            className="block h-6 w-6"
            style={{
              backgroundColor: tint,
              maskImage: `url(${item.iconSrc})`,
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskImage: `url(${item.iconSrc})`,
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
            }}
          />
        ) : item.IconComponent ? (
          <item.IconComponent size={24} color={tint} />
        ) : null}
      </div>
      <span
        className={clsx(
          'mt-0.5 whitespace-pre-line text-center text-xs leading-[14px]',
          isActive ? 'font-semibold text-sidebarTextActive' : 'text-sidebarText',
        )}
      >
        {item.label}
      </span>
    </>
  )
}
